import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../config";
import Backdrop from "../Catalogs/Dialog/Backdrop";
import Spin from "../Spinner/Spinner";
import dialogStyles from "../Users/Carts/CreateCartDialog.module.css";
import styles from "./Groups.module.css";
import { RiDeleteBin6Fill } from "react-icons/ri";

export const CreateGroupDialog = ({
  show = false,
  onClose,
  mode = "create",
  initialGroup = null,
  onSave,
  onUpdate,
}) => {
  const [groupName, setGroupName] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const resetState = () => {
    setGroupName("");
    setSearch("");
    setDebouncedSearch("");
    setUsers([]);
    setSelectedUsers([]);
    setLoadingUsers(false);
  };

  const fetchUsers = async () => {
    if (!show) return;
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/Admin/users`, {
        params: {
          page: 1,
          limit: 10,
          search: debouncedSearch || "",
        },
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!show) {
      resetState();
      return;
    }
    if (mode === "edit" && initialGroup) {
      setGroupName(initialGroup.groupName || "");
      const prefilledUsers = (initialGroup.users || []).map((user) => ({
        ...user,
        _id: user._id || user.id,
      }));
      setSelectedUsers(prefilledUsers);
    }
  }, [show, mode, initialGroup]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!show) return;
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, show]);

  const availableUsers = useMemo(() => {
    const selectedIds = new Set(selectedUsers.map((u) => u._id));
    return (users || []).filter((u) => !selectedIds.has(u._id));
  }, [users, selectedUsers]);

  const handleToggleUser = (user) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u._id === user._id);
      if (exists) {
        return prev.filter((u) => u._id !== user._id);
      }
      return [...prev, { ...user, designation: user.designation || "" }];
    });
  };

  const handleDesignationChange = (userId, value) => {
    setSelectedUsers((prev) =>
      prev.map((u) =>
        u._id === userId
          ? {
              ...u,
              designation: value,
            }
          : u
      )
    );
  };

  const handleRemoveSelectedUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u._id !== userId));
  };

  const handleSaveClick = () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      toast.error("Please enter a group name");
      return;
    }
    if (!selectedUsers.length) {
      toast.error("Please select at least one user");
      return;
    }

    if (mode === "edit" && typeof onUpdate === "function" && initialGroup) {
      onUpdate(initialGroup._id, trimmedName, selectedUsers);
    } else if (typeof onSave === "function") {
      onSave(trimmedName, selectedUsers);
    }
  };

  if (!show) return null;

  return (
    <div>
      <Backdrop show={show} switch={onClose} />
      <div className={dialogStyles.popup}>
        <div className={dialogStyles.header}>
          <h2 className={dialogStyles.title}>Create Group</h2>
          <button
            type="button"
            className={dialogStyles.closeButton}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={dialogStyles.content}>
          <div className={dialogStyles.columnsHeader}>
            <div className={dialogStyles.columnHeader}>Users</div>
            <div className={dialogStyles.columnHeader}>Selected Users</div>
          </div>
          <div className={dialogStyles.columns}>
            <div className={dialogStyles.column}>
              <input
                type="text"
                className={dialogStyles.searchInput}
                placeholder="Group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
              <input
                type="text"
                className={dialogStyles.searchInput}
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className={dialogStyles.listContainer}>
                {loadingUsers ? (
                  <div className={dialogStyles.spinnerWrapper}>
                    <Spin />
                  </div>
                ) : availableUsers.length ? (
                  availableUsers.map((user) => {
                    const isSelected = selectedUsers.some(
                      (u) => u._id === user._id
                    );
                    return (
                      <button
                        key={user._id}
                        type="button"
                        className={dialogStyles.productRow}
                        onClick={() => handleToggleUser(user)}
                      >
                        <div className={dialogStyles.productMain}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className={styles.userCheckbox}
                          />
                          <div className={dialogStyles.productInfo}>
                            <div className={dialogStyles.productName}>
                              {user.name || "-"}
                            </div>
                            <div className={dialogStyles.productMeta}>
                              {user.email || "-"}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className={dialogStyles.emptyState}>No users found</div>
                )}
              </div>
            </div>

            <div className={dialogStyles.column}>
              <div className={dialogStyles.listContainer}>
                {selectedUsers.length ? (
                  selectedUsers.map((user) => (
                    <div key={user._id} className={dialogStyles.cartRow}>
                      <div className={dialogStyles.cartInfo}>
                        <div className={styles.selectedUserHeader}>
                          <div className={dialogStyles.productName}>
                            {user.name || "-"}
                          </div>
                          <button
                            type="button"
                            style={{
                              width: "fit-content",
                              height: "fit-content",
                              margin: 0,
                              padding: 0,
                              border: "none",
                              cursor: "pointer",
                              color: "red",
                              fontSize: "16px",
                              fontWeight: "600",
                              letterSpacing: "0.04em",
                              backgroundColor: "transparent",
                            }}
                            onClick={() => handleRemoveSelectedUser(user._id)}
                            aria-label="Remove user from group"
                          >
                            <RiDeleteBin6Fill />
                          </button>
                        </div>
                        <div className={dialogStyles.productMeta}>
                          {user.email || "-"}
                        </div>
                      </div>
                      <div className={dialogStyles.cartControls}>
                        <div className={styles.designationField}>
                          <label
                            className={dialogStyles.qtyLabel}
                            htmlFor={`designation-${user._id}`}
                          >
                            Designation
                          </label>
                          <input
                            id={`designation-${user._id}`}
                            type="text"
                            className={styles.designationInput}
                            placeholder="Enter designation"
                            value={user.designation || ""}
                            onChange={(e) =>
                              handleDesignationChange(user._id, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={dialogStyles.emptyState}>
                    No users selected yet
                  </div>
                )}
              </div>
              <div className={dialogStyles.footer}>
                <button
                  type="button"
                  className={dialogStyles.cancelButton}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={dialogStyles.confirmButton}
                  onClick={handleSaveClick}
                >
                  Save Group
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
